import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { Z_INDEX } from '@/constants/zIndex';
import { NumberField } from '@/components/Chart/ConfigModalFields';

interface Props {
  isActive: boolean;
  onClose: () => void;
  onAdd?: () => void;
}

// Colors are read live from the store inside the indicator's per-figure
// `styles` callback (see indicators/foreignHolding.ts), and the MA periods
// are read inside calc() itself — both need calc() to actually re-run, not
// just a repaint of its previous output. Passing a changing `calcParams`
// value (unused by calc(), which reads the store instead) is what reliably
// triggers that, unlike an empty `styles: {}` override which only forces a
// redraw — fine for custom draw()-based indicators that re-read external
// data directly in draw(), but not for this plain figures indicator.
function triggerRedraw() {
  const chart = useChartStore.getState().chart;
  const paneId = useChartStore.getState().indicatorPaneMap.get('FOREIGN_HOLDING');
  if (!chart || !paneId) return;
  try {
    (chart as unknown as { overrideIndicator: (o: { name: string; calcParams?: unknown[] }, paneId?: string) => void })
      .overrideIndicator({ name: 'FOREIGN_HOLDING', calcParams: [Date.now()] }, paneId);
  } catch { /* ignore */ }
}

function ColorRow({ label, hint, value, onChange }: { label: string; hint?: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col min-w-0">
        <span className="text-sm text-modal-text">{label}</span>
        {hint && <span className="text-xs text-modal-muted">{hint}</span>}
      </div>
      <ColorPicker value={value} onChange={onChange} />
    </div>
  );
}

export function ForeignHoldingConfigModal({ isActive, onClose, onAdd }: Props) {
  const upColor = useChartStore((s) => s.foreignHoldingUpColor);
  const setUpColor = useChartStore((s) => s.setForeignHoldingUpColor);
  const downColor = useChartStore((s) => s.foreignHoldingDownColor);
  const setDownColor = useChartStore((s) => s.setForeignHoldingDownColor);
  const flatColor = useChartStore((s) => s.foreignHoldingFlatColor);
  const setFlatColor = useChartStore((s) => s.setForeignHoldingFlatColor);
  const ma1Period = useChartStore((s) => s.foreignHoldingMA1Period);
  const setMA1Period = useChartStore((s) => s.setForeignHoldingMA1Period);
  const ma1Color = useChartStore((s) => s.foreignHoldingMA1Color);
  const setMA1Color = useChartStore((s) => s.setForeignHoldingMA1Color);
  const ma2Period = useChartStore((s) => s.foreignHoldingMA2Period);
  const setMA2Period = useChartStore((s) => s.setForeignHoldingMA2Period);
  const ma2Color = useChartStore((s) => s.foreignHoldingMA2Color);
  const setMA2Color = useChartStore((s) => s.setForeignHoldingMA2Color);
  const confirmBullColor = useChartStore((s) => s.foreignHoldingConfirmBullColor);
  const setConfirmBullColor = useChartStore((s) => s.setForeignHoldingConfirmBullColor);
  const distributionColor = useChartStore((s) => s.foreignHoldingDistributionColor);
  const setDistributionColor = useChartStore((s) => s.setForeignHoldingDistributionColor);
  const accumulationColor = useChartStore((s) => s.foreignHoldingAccumulationColor);
  const setAccumulationColor = useChartStore((s) => s.setForeignHoldingAccumulationColor);
  const confirmBearColor = useChartStore((s) => s.foreignHoldingConfirmBearColor);
  const setConfirmBearColor = useChartStore((s) => s.setForeignHoldingConfirmBearColor);

  function handleRemove() {
    const chart = useChartStore.getState().chart;
    const paneMap = useChartStore.getState().indicatorPaneMap;
    const paneId = paneMap.get('FOREIGN_HOLDING');
    if (chart && paneId) {
      try {
        (chart as unknown as { removeIndicator: (paneId: string, name?: string) => void })
          .removeIndicator(paneId, 'FOREIGN_HOLDING');
      } catch { /* ignore */ }
      const next = new Map(paneMap);
      next.delete('FOREIGN_HOLDING');
      useChartStore.getState().setIndicatorPaneMap(next);
      useChartStore.getState().removeActiveIndicator('FOREIGN_HOLDING');
      void useChartStore.getState().rebalancePanes();
    }
    onClose();
  }

  return (
    <Modal onClose={onClose} size="xl" zIndex={Z_INDEX.modalElevated} className="max-h-[min(90vh,680px)]">
      <Modal.Header
        onClose={onClose}
        title={
          <div className="flex items-center gap-2.5">
            <span className="text-base font-bold text-modal-text">Foreign Holding</span>
            <span className="text-sm text-modal-muted">— absolute shares held by foreign investors</span>
            {isActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">
                Active
              </span>
            )}
          </div>
        }
      />

      {/* Body */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="w-[55%] border-r border-border flex flex-col overflow-y-auto">
          <div className="px-5 py-4 flex flex-col gap-4">
            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Bar Colors</p>
            <ColorRow label="Increase" hint="More shares held than the previous snapshot" value={upColor} onChange={(v) => { setUpColor(v); triggerRedraw(); }} />
            <ColorRow label="Decrease" hint="Fewer shares held than the previous snapshot" value={downColor} onChange={(v) => { setDownColor(v); triggerRedraw(); }} />
            <ColorRow label="No change" hint="Same as the previous snapshot" value={flatColor} onChange={(v) => { setFlatColor(v); triggerRedraw(); }} />

            <div className="h-px bg-border" />

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Moving Averages</p>
            <NumberField
              label="MA 1 period" hint="Snapshots averaged (not calendar days)"
              value={ma1Period} onChange={(v) => { setMA1Period(v); triggerRedraw(); }}
              min={2} max={100}
            />
            <ColorRow label="MA 1 color" value={ma1Color} onChange={(v) => { setMA1Color(v); triggerRedraw(); }} />
            <NumberField
              label="MA 2 period" hint="Snapshots averaged (not calendar days)"
              value={ma2Period} onChange={(v) => { setMA2Period(v); triggerRedraw(); }}
              min={2} max={100}
            />
            <ColorRow label="MA 2 color" value={ma2Color} onChange={(v) => { setMA2Color(v); triggerRedraw(); }} />

            <div className="h-px bg-border" />

            <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Divergence Markers</p>
            <ColorRow label="Confirmation (bull)" hint="Price up & holdings up" value={confirmBullColor} onChange={(v) => { setConfirmBullColor(v); triggerRedraw(); }} />
            <ColorRow label="Distribution warning" hint="Price up & holdings down" value={distributionColor} onChange={(v) => { setDistributionColor(v); triggerRedraw(); }} />
            <ColorRow label="Accumulation warning" hint="Price down & holdings up" value={accumulationColor} onChange={(v) => { setAccumulationColor(v); triggerRedraw(); }} />
            <ColorRow label="Confirmation (bear)" hint="Price down & holdings down" value={confirmBearColor} onChange={(v) => { setConfirmBearColor(v); triggerRedraw(); }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">About</p>
          <p className="text-sm text-modal-text leading-relaxed">
            Plots the security's absolute foreign shareholding (shares held by foreign investors)
            as a bar per report date, plus two configurable moving averages of that same series.
            Each bar is colored by comparison to the previous snapshot — up, down, or unchanged.
          </p>
          <p className="text-sm text-modal-text leading-relaxed">
            A small marker is drawn at each snapshot where foreign-holding direction and price
            direction can be compared, colored by whether they confirm or diverge from each other
            — a read on accumulation/distribution independent of the bar's own up/down color.
          </p>
          <p className="text-sm text-modal-text leading-relaxed">
            Exact ownership %, the share and percentage change since the prior snapshot, and the
            absolute count are all shown in the crosshair legend when hovering the pane.
          </p>
          <p className="text-sm text-modal-text leading-relaxed">
            A missing bar on a given day means the underlying report wasn't published that day —
            the source data isn't guaranteed to update every trading day, and both the moving
            averages and divergence markers skip across gaps rather than treating them as a
            normal day-to-day change.
          </p>

          <div className="h-px bg-border" />

          <p className="text-xs font-semibold text-modal-muted uppercase tracking-wider">Legend</p>
          <div className="flex flex-col gap-1.5 text-xs text-modal-muted">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: confirmBullColor }} />
              <span>Confirmation (bull) — price up, holdings up</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: distributionColor }} />
              <span>Distribution warning — price up, holdings down</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: accumulationColor }} />
              <span>Accumulation warning — price down, holdings up</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: confirmBearColor }} />
              <span>Confirmation (bear) — price down, holdings down</span>
            </div>
          </div>
        </div>
      </div>

      <Modal.Footer className="!justify-between">
        {isActive ? (
          <button
            type="button"
            onClick={handleRemove}
            className="text-sm px-3 py-2 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
          >
            Remove Indicator
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-2 rounded-lg text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isActive || !onAdd ? onClose : () => { onAdd(); onClose(); }}
            className="text-sm px-4 py-2 rounded-lg bg-accent text-white font-medium hover:bg-accent/90 transition-colors"
          >
            {isActive ? 'Done' : 'Add to Chart'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
